import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { createHash } from "crypto";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

/**
 * On-the-fly UI translator. Stateless — caller is responsible for caching
 * (the i18n runtime layer caches results in localStorage).
 *
 * Used as a runtime fallback for any UI string that is missing from the
 * pre-generated dictionaries in `src/i18n/generated/<lang>.json`. Lets us
 * ship new English keys without re-running the build-time translator.
 */

const LANG_NAMES: Record<string, string> = {
  fi: "Finnish", sv: "Swedish", de: "German", fr: "French", es: "Spanish",
  it: "Italian", nl: "Dutch", zh: "Simplified Chinese", ja: "Japanese",
  ko: "Korean", ru: "Russian", hi: "Hindi", ta: "Tamil", tr: "Turkish",
};

const Input = z.object({
  lang: z.string().min(2).max(5),
  items: z.record(z.string().min(1).max(120), z.string().max(4000)).refine(
    (o) => Object.keys(o).length > 0 && Object.keys(o).length <= 80,
    "items must contain 1-80 entries",
  ),
});

export const translateUiStrings = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => Input.parse(data))
  .handler(async ({ data }): Promise<{ translations: Record<string, string> }> => {
    const langName = LANG_NAMES[data.lang];
    if (!langName) return { translations: {} };

    const hashOf = (s: string) => createHash("sha256").update(s).digest("hex").slice(0, 32);
    const entries = Object.entries(data.items);
    const hashByKey = new Map<string, string>();
    const keyByHash = new Map<string, string>();
    for (const [k, v] of entries) {
      const h = hashOf(v);
      hashByKey.set(k, h);
      keyByHash.set(h, k);
    }

    const out: Record<string, string> = {};

    // 1) Try server cache first
    try {
      const { data: cached } = await supabaseAdmin
        .from("ui_translations_cache")
        .select("source_hash,value")
        .eq("lang", data.lang)
        .in("source_hash", Array.from(hashByKey.values()));
      for (const row of cached ?? []) {
        const k = keyByHash.get(row.source_hash as string);
        if (k) out[k] = row.value as string;
      }
    } catch (err) {
      console.error("[translateUiStrings] cache read failed:", err);
    }

    // 2) Compute misses
    const missing: Record<string, string> = {};
    for (const [k, v] of entries) if (!(k in out)) missing[k] = v;
    if (Object.keys(missing).length === 0) return { translations: out };

    const lovableKey = process.env.LOVABLE_API_KEY;
    const openaiKey = process.env.OPENAI_API_KEY;
    if (!lovableKey && !openaiKey) {
      console.error("[translateUiStrings] neither LOVABLE_API_KEY nor OPENAI_API_KEY configured");
      return { translations: out };
    }

    const useOpenAI = !lovableKey && !!openaiKey;
    const endpoint = useOpenAI
      ? "https://api.openai.com/v1/chat/completions"
      : "https://ai.gateway.lovable.dev/v1/chat/completions";
    const apiKey = useOpenAI ? openaiKey! : lovableKey!;
    const model = useOpenAI ? "gpt-4o-mini" : "google/gemini-2.5-flash";

    const keys = Object.keys(missing);

    const buildBody = (mdl: string) => JSON.stringify({
      model: mdl,
      messages: [
        {
          role: "system",
          content:
            `You are a professional translator for a Helsinki tourism + lifestyle web app called 'what'. ` +
            `Translate the JSON object's values from English into ${langName} FULLY and NATURALLY. ` +
            `Translate every common English word — including food, drink and venue categories ` +
            `(e.g. "Fine Dining", "Pizza", "Sushi", "Burgers", "Wine", "Cocktails", "Street Food", "Tasting", "Bakery", "Bistro", "Brasserie", "Mediterranean", "Asian", "Vegetarian", "Healthy", "Seaside", "Terrace") — ` +
            `into the natural ${langName} equivalent. Do NOT leave English words mixed into the output. ` +
            `The ONLY things kept unchanged are specific named entities: city/country names (Helsinki, Finland), ` +
            `awards/brands (Michelin), and proper place names (Suomenlinna, Esplanadi). The brand name 'what' stays lowercase. ` +
            `Keep keys EXACTLY as given. Preserve placeholders like {city}, {count}, %s, line breaks, and punctuation. ` +
            `Match the original tone: short, modern, editorial. Do NOT add keys, do NOT translate keys, do NOT explain. ` +
            `Return ONLY the JSON via the provided tool.`,
        },
        {
          role: "user",
          content: "Translate this JSON object's values:\n" + JSON.stringify(missing, null, 2),
        },
      ],
      tools: [
        {
          type: "function",
          function: {
            name: "return_translations",
            description: "Return one translated string per key.",
            parameters: {
              type: "object",
              properties: Object.fromEntries(keys.map((k) => [k, { type: "string" }])),
              required: keys,
              additionalProperties: false,
            },
          },
        },
      ],
      tool_choice: { type: "function", function: { name: "return_translations" } },
    });

    const callOnce = (ep: string, key: string, mdl: string) =>
      fetch(ep, {
        method: "POST",
        headers: { Authorization: `Bearer ${key}`, "Content-Type": "application/json" },
        body: buildBody(mdl),
      });

    try {
      let res = await callOnce(endpoint, apiKey, model);
      // Fallback: if Lovable gateway fails (e.g. self-hosted with no balance / bad key)
      // and an OpenAI key is available, retry against OpenAI directly.
      if (!res.ok && !useOpenAI && openaiKey) {
        const body = await res.text().catch(() => "");
        console.warn(`[translateUiStrings] Lovable AI ${res.status}, falling back to OpenAI: ${body.slice(0, 120)}`);
        res = await callOnce("https://api.openai.com/v1/chat/completions", openaiKey, "gpt-4o-mini");
      }

      if (!res.ok) {
        const body = await res.text().catch(() => "");
        console.error(`[translateUiStrings] AI ${res.status}: ${body.slice(0, 200)}`);
        return { translations: out };
      }

      const json = await res.json();
      const args = json.choices?.[0]?.message?.tool_calls?.[0]?.function?.arguments;
      if (!args) return { translations: out };
      const parsed = typeof args === "string" ? JSON.parse(args) : args;

      const rowsToCache: Array<{ lang: string; source_hash: string; source_text: string; value: string }> = [];
      for (const k of keys) {
        const v = parsed[k];
        if (typeof v === "string" && v.trim().length > 0) {
          out[k] = v;
          rowsToCache.push({
            lang: data.lang,
            source_hash: hashByKey.get(k)!,
            source_text: missing[k],
            value: v,
          });
        }
      }

      if (rowsToCache.length > 0) {
        supabaseAdmin
          .from("ui_translations_cache")
          .upsert(rowsToCache, { onConflict: "lang,source_hash" })
          .then(({ error }) => {
            if (error) console.error("[translateUiStrings] cache write failed:", error);
          });
      }

      return { translations: out };
    } catch (err) {
      console.error("[translateUiStrings] failed:", err);
      return { translations: out };
    }
  });

